import { Upload, DetailedError, type UploadOptions, type PreviousUpload } from "tus-js-client";
import type { OperationalUploadSession } from "@rekonas/domain";
import { apiBaseUrl, authCacheScope } from "@rekonas/data-access";

export interface TusUploadCallbacks {
  onError: (error: Error) => void;
  onProgress: (uploaded: number, total: number) => void;
  onSuccess: () => void;
  onRetry?: (conflict: boolean) => void;
}

interface TusOptionsInput extends TusUploadCallbacks {
  session: OperationalUploadSession;
  token: string;
  file: File;
  identity: string;
}

export class ResumeConflict extends Error {}

export function transportUrl(path: string): string {
  const url = new URL(path, `${apiBaseUrl}/`);
  if (url.origin !== new URL(apiBaseUrl).origin || !url.pathname.startsWith("/uploads/") || url.search || url.hash || url.username || url.password) {
    throw new ResumeConflict("The saved upload destination does not match. Ask an operator to check this upload.");
  }
  return url.toString();
}

export function validateUploadFile(session: OperationalUploadSession, file: File): void {
  if (session.tus.size_bytes !== file.size || session.tus.metadata.filename !== file.name ||
      session.tus.metadata.upload_ref !== session.upload_ref) {
    throw new ResumeConflict("This archive does not match the saved upload. Choose the original archive or ask an operator to check it.");
  }
}

function integerHeader(value: string | undefined, max: number): number {
  if (!value || !/^\d+$/.test(value) || !Number.isSafeInteger(Number(value)) || Number(value) > max) {
    throw new ResumeConflict("The server upload size or position is invalid. Ask an operator to check this upload.");
  }
  return Number(value);
}

export function buildTusUploadOptions(input: TusOptionsInput): UploadOptions {
  validateUploadFile(input.session, input.file);
  let conflicts = 0;
  const scope = authCacheScope();
  const options: UploadOptions = {
    endpoint: transportUrl(input.session.tus.endpoint),
    uploadUrl: input.session.tus.upload_url ? transportUrl(input.session.tus.upload_url) : null,
    uploadSize: input.session.tus.size_bytes,
    headers: { Authorization: `Bearer ${input.token}` },
    metadata: { ...input.session.tus.metadata },
    fingerprint: async () => JSON.stringify(["operational-v2", apiBaseUrl, scope, input.session.upload_ref, input.identity]),
    retryDelays: [0, 1_000, 3_000, 5_000, 10_000],
    chunkSize: 8 * 1024 * 1024,
    storeFingerprintForResuming: true,
    removeFingerprintOnSuccess: true,
    onAfterResponse: (request, response) => {
      const method = request.getMethod();
      const status = response.getStatus();
      if (method === "POST" && status >= 200 && status < 300) {
        const location = response.getHeader("Location");
        if (!location || !transportUrl(new URL(location, request.getURL()).toString()).includes(`/uploads/${input.session.upload_ref}+`)) {
          throw new ResumeConflict("The server returned a different upload identity. Ask an operator to check it.");
        }
      }
      if (method === "HEAD") {
        // Stop tus-js-client's automatic POST fallback on ALL failed HEADs.
        // Recovery must first reconcile the existing product session.
        if (status < 200 || status >= 300) {
          throw Object.assign(new DetailedError("Could not resume this upload."), { originalRequest: request, originalResponse: response, causingError: null });
        }
        const length = integerHeader(response.getHeader("Upload-Length"), input.file.size);
        integerHeader(response.getHeader("Upload-Offset"), length);
        const metadata = Object.fromEntries((response.getHeader("Upload-Metadata") || "").split(",").map((entry) => {
          const [key, value] = entry.trim().split(" ");
          try { return [key, atob(value || "")]; } catch { throw new ResumeConflict("The saved upload metadata is invalid. Ask an operator to check it."); }
        }));
        if (length !== input.file.size || Object.entries(input.session.tus.metadata).some(([key, value]) => metadata[key] !== value)) {
          throw new ResumeConflict("The server upload belongs to a different archive. Ask an operator to check it.");
        }
      }
      if (method === "PATCH" && status >= 200 && status < 300) {
        const offset = integerHeader(response.getHeader("Upload-Offset"), input.file.size);
        if (offset <= Number(request.getHeader("Upload-Offset"))) {
          throw new ResumeConflict("The upload made no progress. Ask an operator to check it.");
        }
      }
    },
    onShouldRetry: (error) => {
      if (error.causingError instanceof ResumeConflict || error instanceof ResumeConflict) return false;
      const { status, method, terminal } = uploadFailure(error);
      if (terminal) return false;
      // A lost creation response must be resolved via the product session, not POST.
      if (method === "POST") return false;
      if (status === 409) {
        conflicts += 1;
        if (method !== "PATCH" || conflicts > 3) return false;
        input.onRetry?.(true);
        return true;
      }
      const retry = status === 0 || status >= 500 || status === 423;
      if (retry) input.onRetry?.(false);
      return retry;
    },
    onError: input.onError,
    onProgress: input.onProgress,
    onSuccess: input.onSuccess,
  };
  return options;
}

export function createTusUpload(input: TusOptionsInput): Upload {
  return new Upload(input.file, buildTusUploadOptions(input));
}

export function findMatchingPreviousUpload(previous: PreviousUpload[], session: OperationalUploadSession): PreviousUpload | undefined {
  const matches = previous.filter((entry) => entry.metadata?.upload_ref === session.upload_ref);
  if (matches.some((entry) => entry.size !== session.tus.size_bytes ||
      Object.entries(session.tus.metadata).some(([key, value]) => entry.metadata[key] !== value) ||
      !entry.uploadUrl || transportUrl(entry.uploadUrl) !== transportUrl(session.tus.upload_url!))) {
    throw new ResumeConflict("The saved browser transfer does not match this upload. Ask an operator to check it.");
  }
  return matches[0];
}

export function uploadFailure(error: Error): { status: number; method?: string; terminal: boolean } {
  if (error instanceof DetailedError) {
    const cause = error.causingError ? uploadFailure(error.causingError) : undefined;
    return { status: error.originalResponse?.getStatus() || cause?.status || 0,
      method: error.originalRequest?.getMethod() || cause?.method, terminal: cause?.terminal || false };
  }
  return { status: 0, terminal: error instanceof ResumeConflict };
}
